import { Box } from "@mui/material";
import { Theme, keyframes, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useColorMode } from "src/design/ColorModeProvider";
import { elevation, radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";

export type ScoreTone = "p" | "n" | "b" | "x" | "m" | "w";

export interface ScoreCellProps {
  tone: ScoreTone;
  studentName: string;
  /** Already formatted, e.g. "32" or "9:10". */
  value: string;
  /** Short event name shown beside the value inside the pill. */
  eventShort: string;
  /** Muted line under the pill (next benchmark, modification). */
  hint?: string;
  /** Just arrived: shows a brief highlight (none under reduced motion). */
  fresh?: boolean;
  onClick?: () => void;
}

const colorsFor = (theme: Theme, tone: ScoreTone) => {
  const p = theme.palette;
  switch (tone) {
    case "p":
      return { bg: p.tierPresidential, fg: p.onTier, line: "transparent" };
    case "n":
      return { bg: p.tierNational, fg: p.onTier, line: "transparent" };
    case "x":
      return { bg: "transparent", fg: p.text.secondary, line: p.divider };
    case "m":
      return { bg: p.warning.light, fg: p.warning.main, line: p.warningLine };
    case "w":
      return { bg: p.surfaceSunken, fg: p.text.secondary, line: p.divider };
    default:
      return { bg: p.surfaceAlt, fg: p.text.primary, line: "transparent" };
  }
};

const flash = keyframes`
  0% { outline-color: currentColor; outline-offset: 0; }
  100% { outline-color: transparent; outline-offset: 6px; }
`;

/**
 * One score in a roster grid (design `CELL`): a pill with the value in condensed numerals and
 * the short event name, plus an optional muted hint under it. The tier is also carried by
 * text, never by color alone.
 */
export const ScoreCell = ({
  tone,
  studentName,
  value,
  eventShort,
  hint,
  fresh = false,
  onClick,
}: ScoreCellProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode, reducedMotion } = useColorMode();
  const { bg, fg, line } = colorsFor(theme, tone);

  const tierWord: Record<ScoreTone, string> = {
    p: t("awards.presidential", { defaultValue: "Presidential" }),
    n: t("awards.national", { defaultValue: "National" }),
    b: t("scoreCell.notYet", { defaultValue: "Not yet" }),
    x: t("scoreCell.excluded", { defaultValue: "Excluded" }),
    m: t("scoreCell.modified", { defaultValue: "Modified" }),
    w: t("scoreCell.waiting", { defaultValue: "Waiting" }),
  };
  const label = `${studentName}, ${eventShort}, ${value}, ${tierWord[tone]}`;
  const glow =
    tone === "p"
      ? elevation.glowPresidential[mode]
      : tone === "n"
        ? elevation.glowNational[mode]
        : "none";
  const inset = line === "transparent" ? "" : `inset 0 0 0 1.5px ${line}`;
  const shadow = [inset, glow !== "none" ? glow : ""]
    .filter(Boolean)
    .join(", ");
  const animate = fresh && !reducedMotion;

  const sx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 0.5,
    boxSizing: "border-box",
    minWidth: 44,
    minHeight: 36,
    px: 1,
    py: 0.75,
    border: 0,
    borderRadius: `${radius.control}px`,
    bgcolor: "transparent",
    color: "text.primary",
    font: "inherit",
    textAlign: "left",
    cursor: onClick ? "pointer" : "default",
    "&:hover": onClick ? { bgcolor: "surfaceSunken" } : undefined,
    outline: fresh ? "2px solid" : "none",
    outlineColor: fresh && !animate ? theme.palette.focusRing : "transparent",
    animation: animate ? `${flash} 1.2s ease-out 1` : "none",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      ...(fresh ? { outlineColor: theme.palette.focusRing } : {}),
    },
  } as const;

  const body = (
    <>
      <Box
        component="span"
        data-testid="score-pill"
        sx={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 0.75,
          px: "11px",
          py: "7px",
          borderRadius: `${radius.pill}px`,
          bgcolor: bg,
          color: fg,
          boxShadow: shadow || "none",
          transition: "all .5s",
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="b"
          sx={{
            fontSize: 15,
            fontWeight: 800,
            lineHeight: 1,
            fontStretch: fontStretch.condensed,
          }}
        >
          {value}
        </Box>
        <Box
          component="span"
          sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1, opacity: 0.85 }}
        >
          {eventShort}
        </Box>
      </Box>
      {hint && (
        <Box
          component="span"
          sx={{
            pl: 0.5,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.2,
            color: "text.secondary",
          }}
        >
          {hint}
        </Box>
      )}
    </>
  );

  if (onClick) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        data-tone={tone}
        data-fresh={fresh ? "true" : "false"}
        data-animated={animate ? "true" : "false"}
        sx={sx}
      >
        {body}
      </Box>
    );
  }
  return (
    <Box
      component="div"
      aria-label={label}
      role="group"
      data-tone={tone}
      data-fresh={fresh ? "true" : "false"}
      data-animated={animate ? "true" : "false"}
      sx={sx}
    >
      {body}
    </Box>
  );
};

export default ScoreCell;
